import { BaseStorage } from './StorageInterface'
import type { Document, DocumentInfo, SearchResult, StorageOptions, DocumentMetadata } from './types'

// Extended metadata for Electron storage
interface ElectronDocumentMetadata extends DocumentMetadata {
  path?: string
}

/**
 * Electron-based storage using IPC for file system operations
 * Single Responsibility: Handle Electron-specific file operations via IPC
 */
export class ElectronStorage extends BaseStorage {
  private api: any
  private vaultPath: string | null = null
  private documents: Map<string, Document> = new Map()
  
  constructor(options: StorageOptions = {}) {
    super(options)
    // Access Electron API via window.api
    this.api = (window as any).api
    if (!this.api) {
      throw new Error('Electron API not available')
    }
  }
  
  /**
   * Initialize storage with a vault directory
   */
  async init(): Promise<void> {
    // Get vault path from vaultStore if available
    const { vaultStore } = await import('$lib/stores/vaultStore')
    const { get } = await import('svelte/store')
    const vault = get(vaultStore)
    
    if (vault.isInitialized && vault.vaultPath) {
      // Use existing vault path
      this.vaultPath = vault.vaultPath
      await this.loadDocuments()
    } else {
      // Request vault directory from user
      const result = await this.api.openFile({
        properties: ['openDirectory'],
        title: 'Select Vault Directory'
      })
      
      if (result.success && result.paths && result.paths[0]) {
        this.vaultPath = result.paths[0]
        await this.loadDocuments()
      } else {
        throw new Error('No vault directory selected')
      }
    }
  }
  
  /**
   * Set vault path directly (when selected via File System Access API)
   */
  async setVaultPath(path: string): Promise<void> {
    this.vaultPath = path
    await this.loadDocuments()
  }
  
  /**
   * Load all documents from vault
   */
  private async loadDocuments(): Promise<void> {
    if (!this.vaultPath) return
    
    try {
      // List all .md files in vault
      const files = await this.api.listFiles(this.vaultPath)
      
      for (const file of files) {
        if (file.endsWith('.md')) {
          const id = this.pathToId(file)
          const content = await this.api.readFile(file)
          
          if (content) {
            const doc: Document = {
              id,
              title: this.extractTitleFromPath(file),
              content: content.content || '',
              createdAt: new Date(content.createdAt || Date.now()),
              updatedAt: new Date(content.updatedAt || Date.now()),
              metadata: {
                path: file,
                ...this.extractMetadata(content.content || '')
              }
            }
            this.documents.set(id, doc)
          }
        }
      }
    } catch (err) {
      console.error('Failed to load documents:', err)
    }
  }
  
  /**
   * Create a new document
   */
  async create(title: string, content = '', metadata?: any): Promise<string> {
    if (!this.vaultPath) throw new Error('No vault initialized')
    
    const id = this.generateId()
    // Keep spaces in filename, only remove truly invalid characters
    const fileName = `${title.replace(/[<>:"/\\|?*]/g, '').trim()}.md`
    const filePath = `${this.vaultPath}/${fileName}`
    
    const doc: Document = {
      id,
      title,
      content,
      createdAt: new Date(),
      updatedAt: new Date(),
      metadata: {
        wordCount: 0,
        characterCount: 0,
        ...metadata,
        path: filePath
      } as ElectronDocumentMetadata
    }
    
    // Save to file system via IPC
    const result = await this.api.writeFile(filePath, content, true)
    
    if (result.success) {
      this.documents.set(id, doc)
      return id
    } else {
      throw new Error(result.error || 'Failed to save document')
    }
  }
  
  /**
   * Load a document by ID
   */
  async load(id: string): Promise<Document> {
    const doc = this.documents.get(id)
    if (!doc) {
      throw new Error(`Document ${id} not found`)
    }
    
    // Reload content from file system
    const metadata = doc.metadata as ElectronDocumentMetadata
    if (metadata?.path) {
      const content = await this.api.readFile(metadata.path)
      if (content && content.content) {
        doc.content = content.content
        doc.updatedAt = new Date(content.updatedAt || Date.now())
      }
    }
    
    return doc
  }
  
  /**
   * Save document changes
   */
  async save(id: string, content: string, metadata?: any): Promise<void> {
    const doc = this.documents.get(id)
    if (!doc) {
      throw new Error(`Document ${id} not found`)
    }

    doc.content = content
    doc.updatedAt = new Date()
    if (metadata) {
      // Update title if provided in metadata
      if (metadata.title) {
        doc.title = metadata.title
      }
      doc.metadata = { ...doc.metadata, ...metadata }
    }

    // Save to file system via IPC
    const docMetadata = doc.metadata as ElectronDocumentMetadata
    if (docMetadata?.path) {
      const result = await this.api.writeFile(docMetadata.path, content)

      if (!result.success) {
        throw new Error(result.error || 'Failed to save document')
      }
    }
  }
  
  /**
   * Delete a document
   */
  async delete(id: string): Promise<void> {
    const doc = this.documents.get(id)
    if (!doc) {
      throw new Error(`Document ${id} not found`)
    }

    // Delete from file system via IPC
    const metadata = doc.metadata as ElectronDocumentMetadata
    if (metadata?.path) {
      const result = await this.api.deleteFile(metadata.path)
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete document')
      }
    }

    this.documents.delete(id)
  }

  /**
   * Rename a document (update id in documents Map)
   * Note: Currently unused as FileSystemStorage is used in Electron
   */
  rename(oldId: string, newId: string, newTitle?: string): void {
    const doc = this.documents.get(oldId)
    if (!doc) {
      return
    }

    // Update document id and title
    doc.id = newId
    if (newTitle) {
      doc.title = newTitle
    }

    // Update metadata path if it exists (convert to absolute path)
    const metadata = doc.metadata as ElectronDocumentMetadata
    if (metadata?.path && this.vaultPath) {
      // Construct new absolute path
      metadata.path = `${this.vaultPath}/${newId}.md`
    }

    // Move to new key in Map
    this.documents.delete(oldId)
    this.documents.set(newId, doc)
  }

  /**
   * List all documents
   */
  async list(): Promise<DocumentInfo[]> {
    const docs: DocumentInfo[] = []
    
    for (const doc of this.documents.values()) {
      docs.push({
        id: doc.id,
        title: doc.title,
        preview: this.extractPreview(doc.content),
        createdAt: doc.createdAt,
        updatedAt: doc.updatedAt,
        wordCount: this.calculateWordCount(doc.content),
        tags: doc.metadata?.tags
      })
    }
    
    return docs.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
  }
  
  /**
   * Search documents
   */
  async search(query: string): Promise<SearchResult[]> {
    const results: SearchResult[] = []
    const queryLower = query.toLowerCase()
    
    for (const doc of this.documents.values()) {
      const contentLower = doc.content.toLowerCase()
      const titleLower = doc.title.toLowerCase()
      
      if (contentLower.includes(queryLower) || titleLower.includes(queryLower)) {
        const score = titleLower.includes(queryLower) ? 1.0 : 0.5
        
        results.push({
          documentId: doc.id,
          title: doc.title,
          preview: this.extractPreview(doc.content),
          score,
          matches: this.extractMatches(doc.content, query)
        })
      }
    }
    
    return results.sort((a, b) => b.score - a.score)
  }
  
  /**
   * Export a document
   */
  async export(id: string, format: 'md' | 'txt' | 'html'): Promise<string> {
    const doc = await this.load(id)
    
    switch (format) {
      case 'html':
        return this.convertToHtml(doc.content)
      case 'txt':
        return this.stripMarkdown(doc.content)
      default:
        return doc.content
    }
  }
  
  /**
   * Import a document
   */
  async import(content: string, format: 'md' | 'txt' | 'html'): Promise<string> {
    let processedContent = content
    
    if (format === 'html') {
      processedContent = this.convertFromHtml(content)
    }
    
    const title = this.extractTitle(processedContent) || 'Imported Document'
    return this.create(title, processedContent)
  }
  
  /**
   * Create a document (compatibility method)
   */
  async createDocument(title: string): Promise<string> {
    return this.create(title, '')
  }
  
  /**
   * Helper: Convert path to ID (use relative path)
   */
  private pathToId(path: string): string {
    // Convert absolute path to relative path for consistent ID format
    if (this.vaultPath && path.startsWith(this.vaultPath)) {
      // Remove vault path prefix and leading slash
      const relativePath = path.substring(this.vaultPath.length).replace(/^\/+/, '')
      // Remove .md extension for ID
      return relativePath.replace(/\.md$/i, '')
    }
    // If already relative, remove .md extension
    return path.replace(/\.md$/i, '')
  }
  
  /**
   * Helper: Extract title from file path
   */
  private extractTitleFromPath(path: string): string {
    const fileName = path.split('/').pop() || 'untitled'
    // Remove .md extension but keep spaces as-is
    return fileName.replace(/\.md$/i, '')
  }
  
  /**
   * Helper: Extract metadata from content
   */
  private extractMetadata(content: string): any {
    const metadata: any = {}
    
    // Check for front matter
    if (content.startsWith('---')) {
      const endIndex = content.indexOf('---', 3)
      if (endIndex > 0) {
        const frontMatter = content.substring(3, endIndex).trim()
        // Simple YAML parsing (basic key: value)
        frontMatter.split('\n').forEach(line => {
          const [key, ...valueParts] = line.split(':')
          if (key && valueParts.length > 0) {
            metadata[key.trim()] = valueParts.join(':').trim()
          }
        })
      }
    }
    
    return metadata
  }
  
  /**
   * Helper: Extract search matches
   */
  private extractMatches(content: string, query: string, maxMatches = 3): string[] {
    const matches: string[] = []
    const lines = content.split('\n')
    const queryLower = query.toLowerCase()
    
    for (const line of lines) {
      if (line.toLowerCase().includes(queryLower)) {
        matches.push(line.trim())
        if (matches.length >= maxMatches) break
      }
    }
    
    return matches
  }
  
  /**
   * Helper: Convert markdown to HTML
   */
  private convertToHtml(markdown: string): string {
    // Basic markdown to HTML conversion
    return markdown
      .replace(/^# (.*)/gm, '<h1>$1</h1>')
      .replace(/^## (.*)/gm, '<h2>$1</h2>')
      .replace(/^### (.*)/gm, '<h3>$1</h3>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br>\n')
  }
  
  /**
   * Helper: Convert HTML to markdown
   */
  private convertFromHtml(html: string): string {
    // Basic HTML to markdown conversion
    return html
      .replace(/<h1>(.*?)<\/h1>/g, '# $1')
      .replace(/<h2>(.*?)<\/h2>/g, '## $1')
      .replace(/<h3>(.*?)<\/h3>/g, '### $1')
      .replace(/<strong>(.*?)<\/strong>/g, '**$1**')
      .replace(/<em>(.*?)<\/em>/g, '*$1*')
      .replace(/<br\s*\/?>/g, '\n')
      .replace(/<[^>]+>/g, '') // Remove remaining HTML tags
  }
  
  /**
   * Helper: Strip markdown formatting
   */
  private stripMarkdown(markdown: string): string {
    return markdown
      .replace(/^#+\s*/gm, '') // Remove headers
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
      .replace(/\*(.*?)\*/g, '$1') // Remove italic
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links
      .replace(/`([^`]+)`/g, '$1') // Remove inline code
  }
  
  /**
   * Check if document exists
   */
  async exists(id: string): Promise<boolean> {
    return this.documents.has(id)
  }
  
  /**
   * Get document info without loading content
   */
  async getInfo(id: string): Promise<DocumentInfo | null> {
    const doc = this.documents.get(id)
    if (!doc) return null
    
    return {
      id,
      title: doc.title,
      preview: this.extractPreview(doc.content),
      createdAt: doc.createdAt,
      updatedAt: doc.updatedAt,
      wordCount: doc.metadata.wordCount,
      tags: doc.metadata.tags,
      isReadError: doc.metadata.isReadError
    }
  }
}